import { Close } from "@carbon/icons-react";
import type { MaterialTagRef } from "../../../types";

// A tag's colour is optional in the data (the API returns null for tags created without one,
// even though the type says `string`). Fall back to a neutral slate so the badge still renders
// instead of crashing on `null.replace(...)`. Matches the last swatch in TagForm's palette.
const FALLBACK_TAG_COLOR = "#64748B";

interface TagBadgeProps {
  // Display-only: this component reads a tag's id, name and colour and nothing else. Typed against
  // the lean `MaterialTagRef` (the shape the material DTO carries) rather than the full
  // `TagResponse` so it renders a material's tags directly; a full `TagResponse` is assignable to
  // it, so its other callers (e.g. TagSelector) are unaffected.
  tag: MaterialTagRef;
  onRemove?: (tagId: string) => void;
  size?: "sm" | "md" | "lg";
  removable?: boolean;
}

const TagBadge = ({
  tag,
  onRemove,
  size = "md",
  removable = false,
}: TagBadgeProps) => {
  const sizeClasses = {
    sm: "text-xxs px-2 py-0.5",
    md: "text-xs px-2.5 py-1",
    lg: "text-sm px-3 py-1.5",
  };

  const iconSizes = {
    sm: 12,
    md: 14,
    lg: 16,
  };

  // Helper function to determine if text should be light or dark based on background
  const getTextColor = (hexColor: string): string => {
    // Convert hex to RGB
    const hex = hexColor.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return white text for dark backgrounds, black for light backgrounds
    return luminance > 0.5 ? "#000000" : "#FFFFFF";
  };

  const color = tag.color || FALLBACK_TAG_COLOR;
  const textColor = getTextColor(color);

  return (
    <span
      className={`inline-flex items-center rounded-full shadow font-medium ${sizeClasses[size]}`}
      style={{
        backgroundColor: color,
        color: textColor,
      }}
    >
      {tag.name}
      {removable && onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(tag.id);
          }}
          className="hover:opacity-70 transition-opacity"
          aria-label={`Remove ${tag.name}`}
        >
          <Close size={iconSizes[size]} />
        </button>
      )}
    </span>
  );
};

export default TagBadge;
